import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
	faArrowRight,
	faFileMagnifyingGlass,
	faScaleBalanced,
	faNetworkWired,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { ROUTE_CONFIG } from "@/routes/config";

const CARD_ICONS = [faFileMagnifyingGlass, faScaleBalanced, faNetworkWired];

type Feature = {
	title: string;
	description: string;
	module: string;
	solution: string;
};

export default function LandingThreeColumns() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const ts = (key: string) => t(`questionnaire.landingThreeColumns.${key}`);

	const features = t("questionnaire.landingThreeColumns.features", {
		returnObjects: true,
	}) as Feature[];

	return (
		<section className="relative w-full px-4 md:px-0 overflow-hidden">
			{/* soft decorative glow */}
			<div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(255,90,36,0.06),transparent_65%)]" />

			<div className="relative w-full max-w-[1420px] mx-auto my-16 sm:my-[7.5rem] flex flex-col items-center gap-14 px-4 md:px-16">
				<div className="flex flex-col items-center gap-4 text-center max-w-[760px]">
					<span className="font-urbanist text-[13px] font-bold uppercase tracking-[0.12em] text-accent-strong bg-accent-tint border border-accent-tint rounded-full px-4 py-1.5">
						{ts("kicker")}
					</span>
					<h2 className="font-urbanist text-[36px] md:text-[40px] font-semibold leading-[120%] text-ink">
						{ts("title")}
					</h2>
					<p className="font-urbanist text-[18px] font-normal leading-[150%] text-subtle">
						{ts("subtitle")}
					</p>
				</div>

				<div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
					{features?.map((feature, index) => (
						<div
							key={feature.title}
							className="group relative flex flex-col gap-5 rounded-2xl border border-border bg-white p-7 shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:border-accent/40 hover:shadow-raised-hover"
						>
							<div className="flex items-center gap-4">
								<div className="flex w-12 h-12 shrink-0 rounded-xl bg-ink justify-center items-center shadow-raised-hover transition-transform duration-300 group-hover:scale-110">
									<Icon className="w-5 h-5 text-white" icon={CARD_ICONS[index]} />
								</div>
								<h3 className="font-urbanist text-[20px] font-bold text-ink leading-[130%]">
									{feature.title}
								</h3>
							</div>

							<div className="flex flex-col gap-2">
								<div className="flex items-center gap-2">
									<span className="w-1.5 h-1.5 rounded-full bg-danger" />
									<span className="font-urbanist text-[12px] font-bold uppercase tracking-[0.1em] text-subtle">
										{ts("problemLabel")}
									</span>
								</div>
								<p className="font-urbanist text-[15px] leading-[160%] text-subtle">
									{feature.description}
								</p>
							</div>

							<div className="mt-auto flex flex-col gap-3 rounded-xl bg-neutral-25 border border-border-soft p-5 transition-colors duration-300 group-hover:bg-accent-tint">
								<div className="flex items-center gap-2">
									<span className="w-1.5 h-1.5 rounded-full bg-success" />
									<span className="font-urbanist text-[12px] font-bold uppercase tracking-[0.1em] text-accent-strong">
										{ts("solutionLabel")}
									</span>
								</div>
								<p className="font-urbanist text-[15px] leading-[160%] text-ink">
									{feature.solution}
								</p>
								<button
									className="flex items-center gap-2 w-fit font-urbanist text-[13px] font-semibold text-accent-strong hover:text-ink transition-colors"
									type="button"
									onClick={() => navigate(ROUTE_CONFIG.PRODUCT)}
								>
									<span className="bg-white border border-accent-tint rounded-full px-3 py-1">
										{feature.module}
									</span>
									<Icon
										className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1"
										icon={faArrowRight}
									/>
								</button>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
